/**
 * PaymentMethodPoliciesPanel — 支付方式策略：启停 + 排序 + Region 覆盖（ADM-PAY-S008）
 *
 * 契约源 dev-docs `02-API契约/A8-运营支付.md` §7.8.0~§7.8.4。挂在 App 详情「支付渠道」tab，
 * 读 `usePaymentMethodPolicies(appId)`（GET §7.8.1），写走 `paymentMethodPoliciesApi`
 * （PUT §7.8.2 / DELETE §7.8.4，均 operator+ mfa；viewer 只读）。
 *
 * **UI 必须让运营区分开的三件事**（§7.8.0，serving 后果各不相同，合并任意两个都会让运营在
 * 「以为关掉了」和「以为恢复继承了」之间踩错）：
 *   1. 组的成员集合 = 该范围的通道启停。全局组的成员集合**就是**该 App 的全局启停。
 *   2. 清空成员（PUT `payment_methods: []`）= 空策略：组保留、成员清空 → 该范围返空态，**不回退全局**。
 *   3. 删除 Region 覆盖（DELETE）= 删组 → 该 Region **恢复继承全局策略**。
 * 故 2 与 3 在 UI 上是两个入口（弹窗内「保存」 vs 列表行「删除覆盖」），文案各自写明后果；
 * 全局组只有 2、没有 3（全局组不可删，§7.8.4 只接受非空 region_code）。
 *
 * **零成员组照常渲染**：组存在且零成员 ≠ 没有这个组，前者返空态、后者回退全局。列表因此对
 * `payment_methods: []` 渲染显式的「空策略」行，不做「空即隐藏」的省略。
 *
 * 排序：`payment_methods` 是**有序数组、数组位置即 sort_order**（服务端按下标 0..n-1 写入），
 * 故编辑态用上移 / 下移直接操作数组顺序，不暴露 sort_order 数值入参。
 *
 * **候选 = §7.8.8 可选支付方式**（ACTIVE 且平台适用的目录行 + 该 App 的 Stripe 同步取值），不提供手输；
 * 结算通道、展示名与二级分组同样取自该列表（`channel` / `label` / `group`）。添加时**先选结算
 * 通道、再勾选该通道下的支付方式**（dd-issue1419；候选按 `admin_group` 分银行卡 / 钱包支付 / 其他）—— 同一通道下的多个支付方式各占一个成员、各配各的图标，
 * 但 placement / 商品 / 权益按通道共用，先选通道让运营看清「这几个选项走的是同一条路」。同步取值标
 * 「Stripe 后台未启用」时仍可加入（写侧不判同步可用性，§7.8 头），只是 C 端暂不展示。
 *
 * **不在可选列表的存量成员**（目录已退役 / 不适用本 App）标「已退役 / 不可选」：整组替换的新成员集合含它即被
 * 服务端拒（§7.8.2「已退役取值」），故编辑弹窗禁用保存、要求先移除；调整顺序（§7.8.10）不判目录状态，照常可用。
 *
 * **收单方 = 选哪一行**（70 §3）：同一面板品牌在不同供应商下是不同目录行（`STRIPE_VISA` / `PAYERMAX_VISA`），
 * 同一策略组内一个品牌只能有一家（§7.8.2「面板品牌唯一」，判定见 `payment-method-panel-brand`）：与成员同品牌的
 * 候选禁用并写明冲突对象，草稿里已有的冲突禁用保存。卡品牌（§7.8.8 `icon_fixed`）用目录固定图标，视同已配图标、
 * 可直接加入；未配图标的非卡品牌候选标「需先配置图标」并链到图标面板。通道候选按 §7.8.8 `channel` 原样分组。
 * 整组换供应商走「切换收单方」（`PaymentMethodAcquirerSwitchDialog`，复用 §7.8.2 整组替换）。
 */

import type {
  PaymentMethod,
  PaymentMethodOption,
  PaymentMethodPolicyResponse,
} from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminApiError } from '~/api/errors';
import {
  type PaymentMethodOptionLookup,
  createPaymentMethodOptionLookup,
} from '~/data/adapters/payment-method-option-lookup';
import {
  type PanelBrandPlatform,
  panelBrandConflictOf,
  panelBrandConflictsIn,
  panelBrandPlatformOf,
} from '~/data/adapters/payment-method-panel-brand';
import {
  paymentMethodPolicyQueryKeys,
  usePaymentMethodOptions,
  usePaymentMethodPolicies,
  usePaymentMethodSettings,
  useSysConfig,
} from '~/data/queries';
import { paymentMethodPoliciesApi } from '~/hooks/use-payment-method-policies';
import { Icons } from '~/lib/icons';
import { useAuthStore } from '~/stores/auth-store';
import { ConfirmDialog, Dialog } from '~/ui/Dialog';
import { FormRow } from '~/ui/FormRow';
import { Section } from '~/ui/Section';
import { Select } from '~/ui/Select';
import { SortableList } from '~/ui/SortableList';
import { toast } from '~/ui/Toast';
import { AcquiringPrerequisiteNote } from './AcquiringPrerequisiteNote';
import { PaymentMethodAcquirerSwitchDialog } from './PaymentMethodAcquirerSwitchDialog';
import { PaymentMethodOrderDialog } from './PaymentMethodOrderDialog';
import { type PaymentMethodOrderItem, PaymentMethodOrderStrip } from './PaymentMethodOrderPreview';
import {
  NOT_SELECTABLE_BADGE,
  PAYMENT_METHOD_GROUP_TITLE,
  channelDisplayLabel,
} from './payment-method-labels';

/**
 * 成员是**支付方式**（`03-数据字典/70-产品配置.md` §3 的 `PAYMENT_METHODS` 层，按 App 求值），
 * 不是结算通道：各 `STRIPE_*` 各占一行、各自排序、各配各的图标。展示名取 §7.8.8 可选列表，与图标面板同源。
 */

const FORBIDDEN_ROLE_TITLE = 'viewer 只读 · 需 operator+ 权限';

/** 图标面板外层锚点 id（AppsPage 挂载处）：候选「需先配置图标」的「去图标面板」据此滚动定位 */
export const PAYMENT_METHOD_ICONS_ANCHOR_ID = 'payment-method-icons';

/**
 * 候选二级组：目录 `admin_group`（70 §3.0：银行卡 / 钱包支付 / 其他）；目录行无分组（如 IAP）归无标题组置顶，
 * Stripe 同步取值没有目录分组，单列「Stripe 同步」组置底。
 */
type CandidateGroupKey = 'UNGROUPED' | keyof typeof PAYMENT_METHOD_GROUP_TITLE | 'STRIPE_SYNC';

const CANDIDATE_GROUP_ORDER: readonly CandidateGroupKey[] = [
  'UNGROUPED',
  'CARD',
  'WALLET',
  'OTHER',
  'STRIPE_SYNC',
];

const CANDIDATE_GROUP_TITLE: Record<CandidateGroupKey, string | null> = {
  UNGROUPED: null,
  CARD: PAYMENT_METHOD_GROUP_TITLE.CARD,
  WALLET: PAYMENT_METHOD_GROUP_TITLE.WALLET,
  OTHER: PAYMENT_METHOD_GROUP_TITLE.OTHER,
  STRIPE_SYNC: 'Stripe 同步',
};

function candidateGroupKeyOf(option: PaymentMethodOption | undefined): CandidateGroupKey {
  if (option?.source === 'STRIPE_SYNC') return 'STRIPE_SYNC';
  const group = option?.group;
  // 闭集外的分组值（目录新增分组而本仓未登记）归无标题组，不让候选从界面上消失
  return group != null && Object.hasOwn(PAYMENT_METHOD_GROUP_TITLE, group) ? group : 'UNGROUPED';
}

/** 同一通道的候选按二级组归并（组内保持可选列表次序），空组不出 */
function groupCandidates(
  methods: readonly PaymentMethod[],
  optionLookup: PaymentMethodOptionLookup,
): { key: CandidateGroupKey; title: string | null; methods: PaymentMethod[] }[] {
  return CANDIDATE_GROUP_ORDER.map((key) => ({
    key,
    title: CANDIDATE_GROUP_TITLE[key],
    methods: methods.filter((method) => candidateGroupKeyOf(optionLookup.optionOf(method)) === key),
  })).filter((group) => group.methods.length > 0);
}

/** AdminApiError / Error → 用户可读文案 */
function errMsg(err: unknown): string {
  return err instanceof Error ? err.message : '操作失败';
}

/**
 * 写动作角色集（root/admin/operator），与 server route
 * `requireRole('root','admin','operator')` 精确一致。前端门控只是 UX，后端才是权威。
 */
function useCanWritePolicy(): boolean {
  const role = useAuthStore((s) => s.user?.role);
  return role === 'root' || role === 'admin' || role === 'operator';
}

/** 编辑目标：已存在的组，或「新增 Region 覆盖」（此时 policy=null，弹窗内先选 Region）*/
interface PolicyEditTarget {
  policy: PaymentMethodPolicyResponse | null;
  /** null = App 全局策略组；新增覆盖时同为 null（由弹窗内选择填充）*/
  regionCode: string | null;
  isNew: boolean;
}

/** 组标题：全局组 vs 某地区覆盖组 */
function policyScopeLabel(regionCode: string | null, regionName?: string): string {
  if (regionCode === null) return '全局策略（默认）';
  return regionName ? `${regionCode} ${regionName} 覆盖` : `${regionCode} 覆盖`;
}

export function PaymentMethodPoliciesPanel({
  appId,
  isWeb = false,
  platform,
}: {
  appId: string;
  isWeb?: boolean;
  /** App 平台（`iOS` / `Android` / `WEB` / 未配置）：钱包键的面板品牌按平台判（70 §3.7.2）；缺省按 `isWeb` 推 */
  platform?: string | null;
}) {
  const brandPlatform: PanelBrandPlatform | null =
    panelBrandPlatformOf(platform) ?? (isWeb ? 'WEB' : null);
  const optionsQuery = usePaymentMethodOptions(appId);
  const options = optionsQuery.data;
  const optionLookup = createPaymentMethodOptionLookup(options);
  const labelOf = optionLookup.labelOf;
  // 候选 = §7.8.8 可选支付方式；未到达时没有候选（不拿任何本地清单兜底）。
  // 平台过滤两路都做：服务端对 WEB 已不列 IAP，这里再兜一次，存量 IAP 成员只能移除、不能被重新加回
  const supportedMethods: PaymentMethod[] = (options?.payment_methods ?? [])
    .map((o) => o.payment_method)
    .filter((method) => !isWeb || method !== 'IAP');
  const unavailableSyncedMethods = new Set(
    (options?.payment_methods ?? [])
      .filter((o) => o.is_available === false)
      .map((o) => o.payment_method),
  );
  const canWrite = useCanWritePolicy();
  const queryClient = useQueryClient();
  const { data: policies, isLoading, isError } = usePaymentMethodPolicies(appId);
  // 图标前置校验（§7.8.2）是服务端权威，这里只把它前移到 UI：没配图标的支付方式加不进成员集合。
  // `isSuccess` 之外一律不禁用 —— 数据没到就禁用等于凭空拦住合法操作，让服务端去判。
  const { data: iconSettings, isSuccess: iconsLoaded } = usePaymentMethodSettings(appId);
  // 地区候选取系统预置清单全量 —— §7.8.2 明写 region_code 须 ∈ system_regions **完整预置清单**，
  // **不按 enabled 过滤**（GET /system-config/regions 本就返回全量行，此处不得再加 enabled 筛选）。
  const { data: sysConfig } = useSysConfig();

  const [editTarget, setEditTarget] = useState<PolicyEditTarget | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PaymentMethodPolicyResponse | null>(null);
  const [orderTarget, setOrderTarget] = useState<PaymentMethodPolicyResponse | null>(null);
  const [switchOpen, setSwitchOpen] = useState(false);
  // 提交前对账（见下方 onSave）是个 await 窗口，其间弹窗仍可能被关掉 —— 而 async 闭包不随弹窗
  // 卸载而中止，会把已撤销的成员集合写进去并弹「已保存」。两道防线：
  //   ① precheckPending 并入 saving → 保存 / 取消按钮置灰、ESC 不响应；
  //   ② editTargetRef 代次校验 → **绕过 saving 的关闭路径**也拦得住。
  // ① 覆盖不全：`~/ui/Dialog` 的 backdrop 与头部 X 按钮都是无条件 onClick={onClose}，改它会波及
  // 全仓所有弹窗，故这两条由 ② 兜底 —— 真正保证「取消后不写库」的是 ②，① 只是把常见路径变灰。
  const [precheckPending, setPrecheckPending] = useState(false);
  const editTargetRef = useRef<PolicyEditTarget | null>(null);
  editTargetRef.current = editTarget;

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: paymentMethodPolicyQueryKeys.byApp(appId) });

  // PUT §7.8.2：建组 + 整组替换成员。payment_methods: [] 是合法入参（空策略），不在此拦。
  const replaceMutation = useMutation({
    mutationFn: (body: { region_code: string | null; payment_methods: PaymentMethod[] }) =>
      paymentMethodPoliciesApi.replace({
        app_id: appId,
        region_code: body.region_code,
        payment_methods: body.payment_methods,
      }),
    onSuccess: async (saved) => {
      const scope = saved.region_code === null ? '全局策略' : `${saved.region_code} 覆盖`;
      toast(
        saved.payment_methods.length === 0
          ? `${scope} · 已保存为空策略（不回退全局）`
          : `${scope} · 已保存 ${saved.payment_methods.length} 个通道`,
        { tone: 'pos', icon: 'IconCheck' },
      );
      setEditTarget(null);
      await invalidate();
    },
    // 失败保留弹窗让运营可改正重试；refetch 对账服务端真值（写可能已部分落地）
    onError: async (err) => {
      toast(`保存通道策略失败 · ${errMsg(err)}`, { tone: 'neg', icon: 'IconAlert' });
      await invalidate();
    },
  });

  // DELETE §7.8.4：删 Region 覆盖组 → 该地区恢复继承全局。与上面的「清空成员」是两个动作。
  const deleteMutation = useMutation({
    mutationFn: (regionCode: string) =>
      paymentMethodPoliciesApi.remove({ app_id: appId, region_code: regionCode }),
    onSuccess: async (removed) => {
      toast(`${removed.region_code} 覆盖已删除 · 该地区恢复继承全局策略`, {
        tone: 'pos',
        icon: 'IconCheck',
      });
      setDeleteTarget(null);
      await invalidate();
    },
    onError: async (err) => {
      toast(`删除 Region 覆盖失败 · ${errMsg(err)}`, { tone: 'neg', icon: 'IconAlert' });
      await invalidate();
    },
  });

  // 调整某个策略组的顺序（§7.8.10 条件调序）：携带打开弹窗时读到的成员集合作预期，服务端锁内核验；
  // 他人在这之间加减过成员 / 删了该覆盖 → RESOURCE_CONFLICT / RESOURCE_NOT_FOUND，不覆盖对方的改动。
  const orderMutation = useMutation({
    mutationFn: (vars: { policy: PaymentMethodPolicyResponse; next: string[] }) =>
      paymentMethodPoliciesApi.reorder(vars.policy.policy_id, {
        expected_payment_methods: vars.policy.payment_methods.map((m) => m.payment_method),
        payment_methods: vars.next as PaymentMethod[],
      }),
    onSuccess: async (saved) => {
      const scope = saved.region_code === null ? '全局策略' : `${saved.region_code} 覆盖`;
      toast(`${scope} · 下发顺序已更新`, { tone: 'pos', icon: 'IconCheck' });
      setOrderTarget(null);
      await invalidate();
    },
    onError: async (err) => {
      const membersChanged =
        err instanceof AdminApiError && err.details?.reason === 'POLICY_MEMBERS_CHANGED';
      toast(
        membersChanged
          ? '该策略组的成员已被他人改动 · 已刷新，请重新调整顺序'
          : `调整顺序失败 · ${errMsg(err)}`,
        { tone: 'neg', icon: 'IconAlert' },
      );
      // 成员已变化 / 组已不存在：弹窗里的草稿基于过期成员，关闭并刷新到服务端真值
      setOrderTarget(null);
      await invalidate();
    },
  });

  const globalPolicy = policies.find((p) => p.region_code === null) ?? null;
  const regionPolicies = policies.filter((p) => p.region_code !== null);
  const usedRegionCodes = new Set(
    regionPolicies.map((p) => p.region_code).filter((c): c is string => c !== null),
  );
  const regionName = (code: string) =>
    sysConfig.regions.find((r) => r.region_code === code)?.display_name_i18n.zh;

  const writeBusy =
    replaceMutation.isPending || deleteMutation.isPending || orderMutation.isPending;
  // 「未配置图标」= 行缺失 或 icon_asset_id 为 null（§7.8.2 同口径），故按 icon_asset_id 判；
  // 卡品牌用目录固定图标（§7.8.8 `icon_fixed`），没有设置行也视同已配（§7.8.2 图标前置校验不适用）。
  const methodsWithIcon = new Set([
    ...iconSettings.filter((row) => row.icon_asset_id !== null).map((row) => row.payment_method),
    ...(options?.payment_methods ?? [])
      .filter((option) => option.icon_fixed)
      .map((option) => option.payment_method),
  ]);
  // 卡品牌只能从可选项认出：两路数据都到齐才据此拦缺图标，任一未就绪传 null（不拦，由服务端判）
  const iconGateReady = iconsLoaded && optionLookup.ready;
  // 预览 / 排序弹窗的成员项：图标与底色取自图标设置，未启用取自可选项的同步可用性
  const orderItemsOfMethods = (methods: readonly string[]): PaymentMethodOrderItem[] =>
    methods.map((method) => {
      const setting = iconSettings.find((row) => row.payment_method === method);
      return {
        method,
        label: labelOf(method),
        iconUrl: setting?.icon_url ?? null,
        backgroundColor: setting?.icon_background_color ?? null,
        unavailable: unavailableSyncedMethods.has(method),
        notSelectable: optionLookup.ready && !optionLookup.isSelectable(method),
        iconFixed: optionLookup.optionOf(method)?.icon_fixed === true,
      };
    });
  const orderItemsOf = (policy: PaymentMethodPolicyResponse): PaymentMethodOrderItem[] =>
    orderItemsOfMethods(policy.payment_methods.map((item) => item.payment_method));
  const scopeLabelOf = (code: string | null) =>
    policyScopeLabel(code, code ? regionName(code) : undefined);

  return (
    <Section
      title="支付通道策略"
      sub="全局策略组的成员集合就是该 App 的通道启停；Region 覆盖组整套替换该地区的成员与顺序（存在即用，零成员也不回退全局）。"
      action={
        <div className="hstack" style={{ gap: 8 }}>
          <button
            type="button"
            className="btn btn-sm"
            disabled={!canWrite || writeBusy || options === null}
            title={
              canWrite
                ? '把某个策略组里一家供应商的各品牌成员整组换成另一家的同品牌支付方式'
                : FORBIDDEN_ROLE_TITLE
            }
            onClick={() => setSwitchOpen(true)}
          >
            <Icons.IconRefresh size={11} />
            切换收单方
          </button>
          <button
            type="button"
            className="btn btn-sm"
            disabled={!canWrite || writeBusy}
            title={canWrite ? '新增一个 Region 覆盖组' : FORBIDDEN_ROLE_TITLE}
            onClick={() => setEditTarget({ policy: null, regionCode: null, isNew: true })}
          >
            <Icons.IconPlus size={11} />
            新增 Region 覆盖
          </button>
        </div>
      }
    >
      {isLoading && (
        <div style={{ padding: '14px 0', fontSize: 11.5, color: 'var(--fg-muted)' }}>
          通道策略加载中…
        </div>
      )}
      {isError && (
        <div style={{ padding: '14px 0', fontSize: 11.5, color: 'var(--neg)' }}>
          通道策略加载失败
        </div>
      )}
      {/* §7.8.8 读取失败时没有任何添加候选 —— 明说并给重试，别让候选无声消失 */}
      {optionsQuery.isError && options === null && (
        <div
          className="hstack"
          style={{ gap: 10, padding: '8px 0', fontSize: 11.5, color: 'var(--warn)' }}
        >
          可选支付方式读取失败：暂无添加候选，成员的展示名暂以原始取值显示。
          <button type="button" className="btn btn-sm" onClick={optionsQuery.refetch}>
            重新读取
          </button>
        </div>
      )}
      {!isLoading && !isError && (
        <>
          {/* 全局组恒首位；组不存在时也渲染一行——「尚未创建」与「零成员」是不同事实，都得能看见 */}
          <PolicyGroupRow
            label={policyScopeLabel(null)}
            hint="该 App 所有未被 Region 覆盖的请求走这条策略"
            policy={globalPolicy}
            items={globalPolicy ? orderItemsOf(globalPolicy) : []}
            canWrite={canWrite}
            busy={writeBusy}
            onReorder={globalPolicy ? () => setOrderTarget(globalPolicy) : null}
            onEdit={() => setEditTarget({ policy: globalPolicy, regionCode: null, isNew: false })}
            /* 全局组不可删（§7.8.4 只接受非空 region_code）；关闭全部通道走「编辑」清空成员 */
            onDelete={null}
          />
          {regionPolicies.map((policy) => {
            const code = policy.region_code as string;
            return (
              <PolicyGroupRow
                key={policy.policy_id}
                label={policyScopeLabel(code, regionName(code))}
                hint="覆盖该商店地区的全部通道与顺序"
                policy={policy}
                items={orderItemsOf(policy)}
                canWrite={canWrite}
                busy={writeBusy}
                onReorder={() => setOrderTarget(policy)}
                onEdit={() => setEditTarget({ policy, regionCode: code, isNew: false })}
                onDelete={() => setDeleteTarget(policy)}
              />
            );
          })}
          {regionPolicies.length === 0 && (
            <div style={{ padding: '10px 0', fontSize: 11.5, color: 'var(--fg-muted)' }}>
              暂无 Region 覆盖：所有地区当前继承全局策略。
            </div>
          )}
        </>
      )}

      {editTarget && (
        <PolicyEditDialog
          appId={appId}
          target={editTarget}
          globalMembers={globalPolicy?.payment_methods.map((m) => m.payment_method) ?? []}
          supportedMethods={supportedMethods}
          optionLookup={optionLookup}
          unavailableSyncedMethods={unavailableSyncedMethods}
          isWeb={isWeb}
          brandPlatform={brandPlatform}
          regionOptions={sysConfig.regions
            // 已有覆盖组的地区不再出现在「新增」候选里（同 region 二次新增等于改既有组，走行内编辑）
            .filter((r) => !usedRegionCodes.has(r.region_code))
            .map((r) => ({ code: r.region_code, name: r.display_name_i18n.zh ?? '' }))}
          saving={replaceMutation.isPending || precheckPending}
          methodsWithIcon={iconGateReady ? methodsWithIcon : null}
          onClose={() => setEditTarget(null)}
          onSave={async (regionCode, members) => {
            // UI 说「这个组还不存在」而 PUT 是 upsert —— 两者之间的窗口里他人可能已把该组建出来，
            // 此时提交等于**静默整组替换**对方刚存的成员集合，而本人收到的是「已保存」。
            // 故凡是从「组不存在」态发起的保存（新增 Region 覆盖 + 全局组尚未创建），提交前都取一次
            // 服务端真值对账。判据是 `policy === null` 而不是 `isNew`：全局组那条入口传的是
            // isNew=false，但 policy 同样可能为 null（列表行明写「尚未创建全局策略组」）。
            // 编辑既有组不在此列 —— 那本来就是 last-write-wins 的整组替换，不是被伪装的覆盖。
            // 本次提交发起时的编辑对象；await 之后据它判断弹窗是否仍是同一次编辑。
            const startedWith = editTarget;
            if (editTarget?.policy == null) {
              setPrecheckPending(true);
              try {
                // staleTime:0 不可省：全局默认 30s，fetchQuery 会直接吃缓存、拿回同一份陈旧快照。
                const latest = await queryClient.fetchQuery({
                  queryKey: paymentMethodPolicyQueryKeys.byApp(appId),
                  queryFn: () => paymentMethodPoliciesApi.list(appId),
                  staleTime: 0,
                });
                if (latest.some((p) => p.region_code === regionCode)) {
                  const scope = regionCode === null ? '全局策略组' : `${regionCode} 覆盖组`;
                  toast(`${scope}已由他人创建 · 已载入最新配置，请在该行上编辑`, {
                    tone: 'warn',
                    icon: 'IconAlert',
                  });
                  setEditTarget(null);
                  await invalidate();
                  return;
                }
              } catch (err) {
                // 对账失败必须停下：fallthrough 直接 mutate 等于绕过刚加的守卫。
                toast(`校验最新配置失败 · ${errMsg(err)} · 请重试`, {
                  tone: 'neg',
                  icon: 'IconAlert',
                });
                return;
              } finally {
                setPrecheckPending(false);
              }
            }
            // 对账期间弹窗被关掉（或已切到另一个组）→ 放弃本次提交。少了这一步，运营的「取消」
            // 会变成一次静默写入 + 一条「已保存」toast。
            if (editTargetRef.current !== startedWith) return;
            replaceMutation.mutate({ region_code: regionCode, payment_methods: members });
          }}
        />
      )}

      {orderTarget && (
        <PaymentMethodOrderDialog
          scopeLabel={policyScopeLabel(
            orderTarget.region_code,
            orderTarget.region_code ? regionName(orderTarget.region_code) : undefined,
          )}
          items={orderItemsOf(orderTarget)}
          saving={orderMutation.isPending}
          onSave={(next) => orderMutation.mutate({ policy: orderTarget, next })}
          onClose={() => {
            if (!orderMutation.isPending) setOrderTarget(null);
          }}
        />
      )}

      {switchOpen && options !== null && (
        <PaymentMethodAcquirerSwitchDialog
          appId={appId}
          policies={policies}
          options={options.payment_methods}
          platform={brandPlatform}
          scopeLabelOf={scopeLabelOf}
          labelOf={labelOf}
          methodsWithIcon={iconGateReady ? methodsWithIcon : null}
          toOrderItems={orderItemsOfMethods}
          saving={replaceMutation.isPending}
          onReread={async (regionCode) => {
            // 契约要求先重读该组（§7.8.1）：staleTime:0 绕过 30s 缓存，顺带刷新列表
            const latest = await queryClient.fetchQuery({
              queryKey: paymentMethodPolicyQueryKeys.byApp(appId),
              queryFn: () => paymentMethodPoliciesApi.list(appId),
              staleTime: 0,
            });
            return latest.find((p) => p.region_code === regionCode) ?? null;
          }}
          onSubmit={(regionCode, finalMembers) =>
            replaceMutation.mutate(
              { region_code: regionCode, payment_methods: finalMembers },
              // 失败保留弹窗（toast 透出服务端 VALIDATION_FAILED 等原因），成功才关
              { onSuccess: () => setSwitchOpen(false) },
            )
          }
          onClose={() => {
            if (!replaceMutation.isPending) setSwitchOpen(false);
          }}
        />
      )}

      {deleteTarget?.region_code && (
        <ConfirmDialog
          title={`删除 ${deleteTarget.region_code} 的 Region 覆盖？`}
          message="删除后该商店地区恢复继承全局策略（覆盖组连同成员一并移除）。"
          details={[
            '这与「清空成员」不同：清空成员会保留该组、让该地区返空态，且不回退全局。',
            '若只是想关闭该地区的全部通道，请改用「编辑」把成员清空后保存。',
          ]}
          tone="warn"
          confirmLabel="确认删除覆盖"
          onConfirm={() => deleteMutation.mutate(deleteTarget.region_code as string)}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </Section>
  );
}

/**
 * 单个策略组行：成员按数组顺序渲染成带序号的 chip。
 * `policy=null` = 该组尚不存在（仅全局组可能出现）；`payment_methods: []` = 空策略。
 * 两者文案必须不同——前者回退不到任何策略、后者是明确关闭。
 */
function PolicyGroupRow({
  label,
  hint,
  policy,
  items,
  canWrite,
  busy,
  onReorder,
  onEdit,
  onDelete,
}: {
  label: string;
  hint: string;
  policy: PaymentMethodPolicyResponse | null;
  /** 成员预览项（按数组顺序，即 C 端展示次序） */
  items: PaymentMethodOrderItem[];
  canWrite: boolean;
  busy: boolean;
  /** null = 该组不存在、没有可排序的成员 */
  onReorder: (() => void) | null;
  onEdit: () => void;
  /** null = 该组不可删（全局组）*/
  onDelete: (() => void) | null;
}) {
  return (
    <FormRow label={label} hint={hint}>
      <div className="hstack" style={{ gap: 10, flexWrap: 'wrap' }}>
        {policy === null ? (
          <span style={{ fontSize: 11.5, color: 'var(--fg-faint)' }}>
            尚未创建全局策略组 —— 该 App 当前不返回任何支付通道
          </span>
        ) : policy.payment_methods.length === 0 ? (
          <span className="chip chip-warn" style={{ fontSize: 10 }}>
            空策略 · 已关闭全部通道（不回退全局）
          </span>
        ) : (
          <PaymentMethodOrderStrip items={items} />
        )}
        <div style={{ flex: 1 }} />
        {onReorder && (
          <button
            type="button"
            className="btn btn-sm"
            aria-label={`调整顺序 ${label}`}
            disabled={!canWrite || busy || items.length < 2}
            title={
              !canWrite
                ? FORBIDDEN_ROLE_TITLE
                : items.length < 2
                  ? '至少 2 个成员才需要排序'
                  : '调整该组的下发顺序'
            }
            onClick={onReorder}
          >
            <Icons.IconGrip size={11} />
            调整顺序
          </button>
        )}
        <button
          type="button"
          className="btn btn-sm"
          /* aria-label 不随角色变化，作为该行的稳定可及名（title 只承载 RBAC 提示）*/
          aria-label={`编辑 ${label}`}
          disabled={!canWrite || busy}
          title={canWrite ? undefined : FORBIDDEN_ROLE_TITLE}
          onClick={onEdit}
        >
          <Icons.IconEdit size={11} />
          编辑
        </button>
        {onDelete && (
          <button
            type="button"
            className="btn btn-sm btn-danger"
            aria-label={`删除覆盖 ${label}`}
            disabled={!canWrite || busy}
            title={canWrite ? '删除该地区覆盖 · 恢复继承全局' : FORBIDDEN_ROLE_TITLE}
            onClick={onDelete}
          >
            <Icons.IconTrash size={11} />
            删除覆盖
          </button>
        )}
      </div>
    </FormRow>
  );
}

/**
 * 策略编辑弹窗：选通道 + 调顺序 → PUT 整组替换。
 * 顺序即优先级（数组下标 = sort_order），故只提供上移 / 下移，不暴露 sort_order 数值。
 * 成员清空到零是**合法保存**（空策略），不在前端拦——但保存前用醒目提示写清它与「删除覆盖」的区别。
 */
function PolicyEditDialog({
  appId,
  target,
  globalMembers,
  supportedMethods,
  optionLookup,
  unavailableSyncedMethods,
  isWeb,
  brandPlatform,
  regionOptions,
  saving,
  methodsWithIcon,
  onSave,
  onClose,
}: {
  /** 所属 App：框架通道 tab 的收单前置按它取配置行 */
  appId: string;
  target: PolicyEditTarget;
  /** 全局策略组当前的成员（按顺序）；新增 Region 覆盖时可「从全局复制」 */
  globalMembers: readonly PaymentMethod[];
  /** §7.8.8 可选支付方式（目录行 + 同步取值）；只从这里选，不手输 */
  supportedMethods: readonly PaymentMethod[];
  /** §7.8.8 查表：结算通道、展示名、是否仍可选 */
  optionLookup: PaymentMethodOptionLookup;
  /** 同步但当前不下发的取值（可加入，标示即可）*/
  unavailableSyncedMethods: Set<string>;
  isWeb: boolean;
  /** 面板品牌判定平台（钱包键按平台判同品牌） */
  brandPlatform: PanelBrandPlatform | null;
  /** 新增覆盖时的地区候选（系统预置全量减去已有覆盖组的地区，**不按 enabled 过滤**）*/
  regionOptions: { code: string; name: string }[];
  saving: boolean;
  /** 已满足图标要求的支付方式（含卡品牌固定图标）；`null` = 设置数据未就绪，此时不禁用任何候选（服务端仍会拦）*/
  methodsWithIcon: Set<string> | null;
  onSave: (regionCode: string | null, members: PaymentMethod[]) => void | Promise<void>;
  onClose: () => void;
}) {
  const labelOf = optionLookup.labelOf;
  // 新增覆盖默认「从全局复制」（全局有成员时）：覆盖是整组替换，从全局起步比从空白搭建更安全
  // 复制保留全局**完整有序**成员：候选（可选支付方式）可能还没加载完，拿它过滤会把同步取值静默丢掉，
  // 而覆盖是整组替换、不回退全局，丢了就等于关掉了那些支付方式。不适用本平台的旧异常成员（如 Web 的 IAP）
  // 照常带入，由下方 hasUnsupportedMember 禁用保存并要求先移除。
  const copyableFromGlobal = [...globalMembers];
  const [initMode, setInitMode] = useState<'copy' | 'empty'>(
    copyableFromGlobal.length > 0 ? 'copy' : 'empty',
  );
  const [members, setMembers] = useState<PaymentMethod[]>(
    target.policy?.payment_methods.map((m) => m.payment_method) ??
      (target.isNew && initMode === 'copy' ? copyableFromGlobal : []),
  );
  const [regionCode, setRegionCode] = useState<string>('');
  // 「去图标面板」「去 {通道} 配置」都会离开本弹窗：草稿有改动时先确认，免得勾选 / 排序 / 新覆盖地区被静默丢掉
  const [baselineMembers] = useState(members);
  const draftDirty =
    regionCode !== '' ||
    members.length !== baselineMembers.length ||
    members.some((method, index) => method !== baselineMembers[index]);
  const navigate = useNavigate();
  // 待确认的离开目标：图标面板（关弹窗并滚动）或框架通道配置页（路由跳转，弹窗随 App 详情卸载）
  const [pendingLeave, setPendingLeave] = useState<
    { kind: 'icons' } | { kind: 'config'; to: string } | null
  >(null);
  const goIcons = () => {
    onClose();
    // 等弹窗卸载、焦点归还触发按钮之后再滚动，否则焦点恢复会把视口拉回策略面板
    window.setTimeout(() => {
      document
        .getElementById(PAYMENT_METHOD_ICONS_ANCHOR_ID)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  // 先选结算通道、再选支付方式：通道 = 可选支付方式所属的通道（WEB 无 IAP 通道）。
  // 通道取 §7.8.8 `channel` 原样、按可选列表次序（即目录 catalog_order）：admin 结算通道闭集外的
  // 通道值同样是合法收单方，不能因展示名缺登记就不给选。
  const methodsByChannel = new Map<string, PaymentMethod[]>();
  for (const method of supportedMethods) {
    const channel = optionLookup.optionOf(method)?.channel;
    if (channel === undefined) continue;
    methodsByChannel.set(channel, [...(methodsByChannel.get(channel) ?? []), method]);
  }
  const channelChoices = [...methodsByChannel.keys()];
  const [pickedChannel, setPickedChannel] = useState('');
  const activeChannel =
    pickedChannel !== '' && methodsByChannel.has(pickedChannel)
      ? pickedChannel
      : (channelChoices[0] ?? null);
  // 该通道的候选按目录 `admin_group` 分二级组（银行卡 / 钱包支付 / 其他）；勾选 = 在本组成员里
  const candidateGroups = groupCandidates(
    activeChannel === null ? [] : (methodsByChannel.get(activeChannel) ?? []),
    optionLookup,
  );
  // 未配图标的候选不能加入（§7.8.2 图标前置校验；卡品牌 `icon_fixed` 已计入 methodsWithIcon）
  const isBlocked = (method: PaymentMethod) =>
    methodsWithIcon !== null && !methodsWithIcon.has(method);
  // 与现有成员同一面板品牌的候选（§7.8.2「面板品牌唯一」）：禁用并写明冲突对象
  const brandConflictOf = (method: PaymentMethod) =>
    panelBrandConflictOf(method, members, optionLookup.optionOf, brandPlatform);
  const toggleMember = (method: PaymentMethod) =>
    setMembers((prev) =>
      prev.includes(method) ? prev.filter((m) => m !== method) : [...prev, method],
    );
  // 草稿内已有的同品牌冲突（存量数据或并发写入）：保存必被拒，禁用保存并说明
  const memberBrandConflicts = panelBrandConflictsIn(members, optionLookup.optionOf, brandPlatform);
  const conflictingMembers = new Set(memberBrandConflicts.map((conflict) => conflict.member));
  // 草稿里仍有未配图标的成员 → 本次保存必被 §7.8.2 拒。禁用保存并说清两条出路（去配图标 / 先移出），
  // 比让运营撞一次 422 更直接；**只按草稿判**，故「把缺图标的成员移出」这条修复路径不会被自己挡住。
  const membersMissingIcon =
    methodsWithIcon === null ? [] : members.filter((m) => !methodsWithIcon.has(m));
  // 旧异常成员保留为可移除项，不能静默丢弃，也不能重新保存到 Web 策略。
  const hasUnsupportedMember = isWeb && members.includes('IAP');
  // 不在可选列表的成员（目录已退役等）：整组替换含它必被 §7.8.2 拒。只在列表已到达时判，未到达不拦（服务端仍会拦）。
  // Web 的 IAP 已由 hasUnsupportedMember 给出更具体的原因，这里不重复计。
  const notSelectableMembers = optionLookup.ready
    ? members.filter((m) => !optionLookup.isSelectable(m) && !(isWeb && m === 'IAP'))
    : [];
  const effectiveRegion = target.isNew ? regionCode || null : target.regionCode;
  const isGlobal = !target.isNew && target.regionCode === null;

  const scopeText = isGlobal
    ? '该 App 全局'
    : effectiveRegion
      ? `${effectiveRegion} 地区`
      : '该地区';
  const canSave =
    !saving &&
    (!target.isNew || regionCode !== '') &&
    membersMissingIcon.length === 0 &&
    !hasUnsupportedMember &&
    notSelectableMembers.length === 0 &&
    memberBrandConflicts.length === 0;

  return (
    <Dialog
      /* 对账 / 提交在途时不许 ESC 关闭（backdrop 那条路由 onSave 里的代次校验兜底）*/
      closeOnEsc={!saving && pendingLeave === null}
      title={
        target.isNew
          ? '新增 Region 覆盖'
          : isGlobal
            ? '编辑全局策略'
            : `编辑 ${target.regionCode} 覆盖`
      }
      subtitle="先选通道、再选支付方式 · 通道顺序即客户端展示优先级；整组替换，保存后以此列表为准。"
      icon="IconWallet"
      width={620}
      onClose={onClose}
      footer={
        <>
          <div style={{ flex: 1, fontSize: 11, color: 'var(--fg-muted)' }}>
            已选 {members.length} 项
          </div>
          <button type="button" className="btn" disabled={saving} onClick={onClose}>
            取消
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!canSave}
            title={
              hasUnsupportedMember
                ? '该平台不支持 IAP，请先移除应用内支付成员'
                : notSelectableMembers.length > 0
                  ? `${notSelectableMembers.map((m) => labelOf(m)).join('、')} ${NOT_SELECTABLE_BADGE}，请先移除`
                  : target.isNew && regionCode === ''
                    ? '请先选择要覆盖的商店地区'
                    : membersMissingIcon.length > 0
                      ? `${membersMissingIcon.map((m) => labelOf(m)).join('、')} 尚未配置图标 —— 请先在上方「支付方式图标」上传，或把它移出本组`
                      : memberBrandConflicts.length > 0
                        ? '组内有同一面板品牌的两家供应商，请先移除其一'
                        : undefined
            }
            onClick={() => void onSave(effectiveRegion, members)}
          >
            <Icons.IconCheck size={11} />
            保存策略
          </button>
        </>
      }
    >
      <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {target.isNew && (
          <FormRow
            label="商店地区"
            hint="候选为系统预置地区全量清单（含未启用地区）；可输入代码或名称快速筛选；已有覆盖的地区请在列表里直接编辑"
          >
            <Select
              value={regionCode}
              onChange={(e) => setRegionCode(e.target.value)}
              placeholder="选择商店地区"
              name="region_code"
              searchable
              searchPlaceholder="输入地区代码或名称筛选…"
            >
              {regionOptions.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.code} {r.name}
                </option>
              ))}
            </Select>
          </FormRow>
        )}

        {target.isNew && (
          <FormRow
            label="初始成员"
            hint="覆盖建立后与全局解耦：全局后续的成员与顺序改动不再影响这份覆盖"
          >
            <div className="seg">
              <button
                type="button"
                className={initMode === 'copy' ? 'on' : ''}
                disabled={saving || copyableFromGlobal.length === 0}
                title={copyableFromGlobal.length === 0 ? '全局策略暂无可复制的成员' : undefined}
                onClick={() => {
                  setInitMode('copy');
                  setMembers(copyableFromGlobal);
                }}
              >
                从全局复制
              </button>
              <button
                type="button"
                className={initMode === 'empty' ? 'on' : ''}
                disabled={saving}
                onClick={() => {
                  setInitMode('empty');
                  setMembers([]);
                }}
              >
                直接创建空覆盖
              </button>
            </div>
          </FormRow>
        )}

        <div>
          <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginBottom: 8 }}>
            已启用通道（自上而下即优先级）
          </div>
          {members.length === 0 ? (
            <div style={{ fontSize: 11.5, color: 'var(--fg-faint)', padding: '6px 0' }}>
              当前没有任何通道。
            </div>
          ) : (
            <SortableList
              items={members}
              getKey={(method) => method}
              itemLabel={labelOf}
              onReorder={setMembers}
              disabled={saving}
              renderContent={(method) => (
                <div className="hstack" style={{ gap: 8 }}>
                  <span style={{ fontSize: 12 }}>{labelOf(method)}</span>
                  {/* 已在成员里但没图标 —— 保存必被 §7.8.2 拒。存量 App 的默认状态就是这个，
                      只标「可添加」候选是不够的：运营不动候选、只调顺序照样存不下去。 */}
                  {methodsWithIcon !== null && !methodsWithIcon.has(method) && (
                    <span className="chip chip-warn" style={{ fontSize: 10 }}>
                      缺图标
                    </span>
                  )}
                  {unavailableSyncedMethods.has(method) && (
                    <span className="chip chip-neg" style={{ fontSize: 10 }}>
                      Stripe 后台未启用
                    </span>
                  )}
                  {optionLookup.ready && !optionLookup.isSelectable(method) && (
                    <span className="chip chip-neg" style={{ fontSize: 10 }}>
                      {NOT_SELECTABLE_BADGE}
                    </span>
                  )}
                  {conflictingMembers.has(method) && (
                    <span className="chip chip-neg" style={{ fontSize: 10 }}>
                      品牌重复
                    </span>
                  )}
                </div>
              )}
              renderActions={(method) => (
                <button
                  type="button"
                  className="btn btn-sm btn-ghost btn-icon"
                  disabled={saving}
                  title={`移除 ${labelOf(method)}`}
                  aria-label={`移除 ${labelOf(method)}`}
                  onClick={() => setMembers((prev) => prev.filter((m) => m !== method))}
                >
                  <Icons.IconX size={11} />
                </button>
              )}
            />
          )}
        </div>

        {activeChannel !== null && (
          <div className="vstack" style={{ gap: 12 }}>
            <div style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
              添加支付方式：先选结算通道，再勾选该通道下的支付方式（勾选即加入、取消即移出）
            </div>
            <div className="seg" role="tablist" aria-label="结算通道">
              {channelChoices.map((channel) => (
                <button
                  key={channel}
                  type="button"
                  role="tab"
                  aria-selected={channel === activeChannel}
                  className={channel === activeChannel ? 'on' : ''}
                  disabled={saving}
                  onClick={() => setPickedChannel(channel)}
                >
                  {channelDisplayLabel(channel)}
                </button>
              ))}
            </div>
            {candidateGroups.map((group) => (
              <div key={group.key}>
                {group.title && (
                  <div className="label" style={{ fontSize: 10, marginBottom: 6 }}>
                    {group.title}
                  </div>
                )}
                <div className="vstack" style={{ gap: 4 }}>
                  {group.methods.map((method) => (
                    <PolicyCandidateRow
                      key={method}
                      label={labelOf(method)}
                      option={optionLookup.optionOf(method)}
                      isMember={members.includes(method)}
                      conflictWith={members.includes(method) ? null : brandConflictOf(method)}
                      missingIcon={isBlocked(method)}
                      iconKnown={methodsWithIcon !== null}
                      unavailable={unavailableSyncedMethods.has(method)}
                      saving={saving}
                      conflictLabelOf={labelOf}
                      onToggle={() => toggleMember(method)}
                      onGoIcons={() => {
                        // 提交在途不离开（与取消按钮 / Esc 同一保护）：在途 PUT 照常落地，「不会保存」的确认会说反
                        if (saving) return;
                        if (draftDirty) setPendingLeave({ kind: 'icons' });
                        else goIcons();
                      }}
                    />
                  ))}
                </div>
              </div>
            ))}
            {/* 框架通道：成员加进来也要配置就绪才会下发 —— 与切换收单方同一判据，只提示不阻断 */}
            <AcquiringPrerequisiteNote
              appId={appId}
              channel={activeChannel}
              isNativeApp={brandPlatform === null ? null : brandPlatform !== 'WEB'}
              actionLabel="保存"
              onConfigLinkClick={(to, event) => {
                // 修饰键 / 中键 = 新标签页打开，本页不离开、草稿不丢，不拦
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
                // 提交在途不离开；有未保存草稿先确认，确认后再跳
                if (saving) {
                  event.preventDefault();
                  return;
                }
                if (draftDirty) {
                  event.preventDefault();
                  setPendingLeave({ kind: 'config', to });
                }
              }}
            />
          </div>
        )}

        {/* 成员缺图标 → 保存必被拒。disabled 按钮的 title 在部分浏览器不触发，故这里明写一块 */}
        {membersMissingIcon.length > 0 && (
          <div
            style={{
              padding: '10px 12px',
              borderRadius: 7,
              background: 'var(--warn-soft)',
              color: 'var(--warn)',
              fontSize: 11.5,
              lineHeight: 1.6,
            }}
          >
            <strong>{membersMissingIcon.map((m) => labelOf(m)).join('、')}</strong>{' '}
            尚未配置图标，本次保存会被服务端拒绝（A8 §7.8.2「策略成员 ⇒ 有图标」）。
            请先在上方「支付方式图标」上传，或把它从本组移出。
          </div>
        )}

        {/* 不在可选列表的成员 → 保存必被拒（§7.8.2「已退役取值」）；disabled 按钮的 title 不可靠，明写一块 */}
        {notSelectableMembers.length > 0 && (
          <div
            style={{
              padding: '10px 12px',
              borderRadius: 7,
              background: 'var(--warn-soft)',
              color: 'var(--warn)',
              fontSize: 11.5,
              lineHeight: 1.6,
            }}
          >
            <strong>{notSelectableMembers.map((m) => labelOf(m)).join('、')}</strong>{' '}
            已不在可选支付方式列表（目录已退役或不适用本
            App），整组保存会被服务端拒绝。请先把它从本组移出。
          </div>
        )}

        {/* 同品牌两家供应商 → 保存必被拒（§7.8.2「面板品牌唯一」）；disabled 按钮的 title 不可靠，明写一块 */}
        {memberBrandConflicts.length > 0 && (
          <div
            style={{
              padding: '10px 12px',
              borderRadius: 7,
              background: 'var(--warn-soft)',
              color: 'var(--warn)',
              fontSize: 11.5,
              lineHeight: 1.6,
            }}
          >
            <strong>
              {memberBrandConflicts
                .map(({ earlier, member }) => `${labelOf(earlier)} 与 ${labelOf(member)}`)
                .join('；')}
            </strong>{' '}
            是同一面板品牌，同一策略组内一个品牌只能有一家供应商，整组保存会被服务端拒绝。请先移除其一。
          </div>
        )}

        {/* 空策略是合法保存，但后果与「删除覆盖」相反 —— 保存前必须把这点说清楚 */}
        {members.length === 0 && (
          <div
            style={{
              padding: '10px 12px',
              borderRadius: 7,
              background: 'var(--warn-soft)',
              color: 'var(--warn)',
              fontSize: 11.5,
              lineHeight: 1.6,
            }}
          >
            保存后为<strong>空策略</strong>：组保留、成员清空 → {scopeText}不返回任何支付通道，
            <strong>不回退全局策略</strong>。
            {isGlobal
              ? '全局策略组不可删除，清空成员是关闭该 App 全部通道的唯一方式。'
              : '若想让该地区恢复继承全局策略，请关闭本弹窗改用列表行的「删除覆盖」。'}
          </div>
        )}
      </div>
      {pendingLeave && (
        <ConfirmDialog
          title={
            pendingLeave.kind === 'icons'
              ? '放弃未保存的改动并前往图标面板？'
              : '放弃未保存的改动并前往通道配置？'
          }
          message={
            pendingLeave.kind === 'icons'
              ? '前往「支付方式图标」会关闭本弹窗，本次勾选、排序与地区选择都不会保存。'
              : '前往通道配置页会离开 App 详情并关闭本弹窗，本次勾选、排序与地区选择都不会保存。'
          }
          tone="warn"
          confirmLabel="放弃改动并前往"
          onConfirm={() => {
            const target = pendingLeave;
            setPendingLeave(null);
            if (target.kind === 'icons') goIcons();
            else navigate(target.to);
          }}
          onClose={() => setPendingLeave(null)}
        />
      )}
    </Dialog>
  );
}

/**
 * 策略编辑弹窗的一行候选：勾选 = 本组成员。只拦「加入」：同面板品牌冲突、未配图标的非成员禁用并写明原因；
 * 已是成员的照常可取消勾选（移出是修复冲突 / 缺图标的出路，不能被自己挡住）。
 */
function PolicyCandidateRow({
  label,
  option,
  isMember,
  conflictWith,
  missingIcon,
  iconKnown,
  unavailable,
  saving,
  conflictLabelOf,
  onToggle,
  onGoIcons,
}: {
  label: string;
  option: PaymentMethodOption | undefined;
  isMember: boolean;
  /** 与之同一面板品牌的现有成员；`null` = 无冲突（成员自身恒传 null） */
  conflictWith: PaymentMethod | null;
  /** 已知未配图标（卡品牌 `icon_fixed` 不算）；图标数据未就绪时为 false，交服务端判 */
  missingIcon: boolean;
  /** 图标设置与可选列表都已到达（未到达时不宣称「已配图标」） */
  iconKnown: boolean;
  /** Stripe 同步取值当前不下发 */
  unavailable: boolean;
  saving: boolean;
  conflictLabelOf: (method: string) => string;
  onToggle: () => void;
  onGoIcons: () => void;
}) {
  const blockedAdd = !isMember && (conflictWith !== null || missingIcon);
  const disabled = saving || blockedAdd;
  const title = isMember
    ? `移出 ${label}`
    : conflictWith !== null
      ? `${label} 与成员「${conflictLabelOf(conflictWith)}」是同一面板品牌 · 同一策略组内一个品牌只能有一家供应商，请先移除「${conflictLabelOf(conflictWith)}」或用「切换收单方」`
      : missingIcon
        ? `${label} 尚未配置图标 · 请先在上方「支付方式图标」配好再加入`
        : unavailable
          ? `添加 ${label} · Stripe 侧当前未开启，加入后暂不下发`
          : `添加 ${label}`;
  return (
    <label
      className="hstack"
      style={{
        gap: 10,
        padding: '7px 10px',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        background: blockedAdd ? 'var(--bg-subtle)' : undefined,
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      <input
        type="checkbox"
        checked={isMember}
        disabled={disabled}
        aria-label={label}
        title={title}
        onChange={onToggle}
      />
      <span style={{ flex: 1, fontSize: 12.5, color: blockedAdd ? 'var(--fg-faint)' : undefined }}>
        {label}
      </span>
      {conflictWith !== null ? (
        <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
          与成员「{conflictLabelOf(conflictWith)}」是同一面板品牌，请先移除或用「切换收单方」
        </span>
      ) : missingIcon ? (
        <>
          <span className="chip chip-warn" style={{ fontSize: 10 }}>
            需先配置图标
          </span>
          <a
            href={`#${PAYMENT_METHOD_ICONS_ANCHOR_ID}`}
            style={{ fontSize: 11 }}
            title="关闭本弹窗并定位到「支付方式图标」（本次未保存的改动不保留；保存中不可离开）"
            onClick={(e) => {
              e.preventDefault();
              onGoIcons();
            }}
          >
            去图标面板
          </a>
        </>
      ) : unavailable ? (
        <span style={{ fontSize: 11, color: 'var(--neg)' }}>Stripe 后台未启用</span>
      ) : option?.icon_fixed ? (
        <span className="chip" style={{ fontSize: 10 }}>
          固定图标
        </span>
      ) : option?.source === 'FIXED' && option.method_key === null ? (
        <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
          {iconKnown ? '无面板品牌 · 已配图标' : '无面板品牌'}
        </span>
      ) : null}
    </label>
  );
}
